import * as React from 'react';
import { debounce, usePrevious } from '@core-front/helper';
const ObjectFitCore = (props) => {
    let imageRef;
    const [browserSupport, setBrowserSupport] = React.useState(true);
    const [imageHeight, setImageHeight] = React.useState(0);
    const [imageWidth, setImageWidth] = React.useState(0);
    const [style, setObject] = React.useState({});
    const prevWidth = usePrevious(imageWidth);
    const prevHeight = usePrevious(imageHeight);
    React.useEffect(() => {
        if (prevWidth !== imageWidth || prevHeight !== imageHeight) {
            setObjectFitStyles();
        }
    });
    React.useEffect(() => {
        if (!('objectFit' in document.body.style) && browserSupport) {
            setBrowserSupport(false);
        }
    }, [browserSupport]);
    const BEM = (className) => {
        const classArray = ['object-fit'];
        const singlePositionAxis = (props.positionX && props.positionX) || (props.positionY && props.positionY) || undefined;
        if (props.fit) {
            classArray.push(`object-fit--${props.fit}`);
        }
        if (props.positionX && props.positionY) {
            classArray.push(`object-position--${props.positionY}-${props.positionX}`);
        }
        else if (singlePositionAxis) {
            classArray.push(`object-position--${singlePositionAxis}`);
        }
        if (props.className) {
            classArray.push(props.className);
        }
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const setContentStyleProperty = (property, value) => {
        const newStyle = {
            [property]: value,
        };
        setObject(prevState => (Object.assign(Object.assign({}, prevState), newStyle)));
    };
    const onLoad = (event) => {
        const target = event.target;
        if (target instanceof HTMLImageElement && imageHeight !== target.naturalHeight) {
            setImageHeight(target.naturalHeight);
        }
        if (target instanceof HTMLImageElement && imageWidth !== target.naturalWidth) {
            setImageWidth(target.naturalWidth);
        }
    };
    const setObjectPosition = (posX, posY) => {
        if (posX && posY) {
            return posX.concat(' ' + posY);
        }
        return posX || posY;
    };
    const setObjectFitStyles = () => {
        if (browserSupport) {
            return;
        }
        const { fit, positionX, positionY } = props;
        const position = setObjectPosition(positionX, positionY);
        if (imageRef && imageRef.parentElement && !browserSupport) {
            const parentClientWidth = imageRef.parentElement.clientWidth;
            const parentClientHeight = imageRef.parentElement.clientHeight;
            const fitImageWidth = (imageWidth / imageHeight) * parentClientHeight;
            const fitImageHeight = (imageHeight / imageWidth) * parentClientWidth;
            // Reset any pre-set CSS
            setContentStyleProperty('position', 'absolute');
            setContentStyleProperty('width', 'auto');
            setContentStyleProperty('height', 'auto');
            if (fit === 'fill') {
                setContentStyleProperty('width', '100%');
                setContentStyleProperty('height', '100%');
                if (position) {
                    setPosition(parentClientWidth, 'x', position);
                    setPosition(parentClientHeight, 'y', position);
                }
                return;
            }
            else if ((fit === 'cover' && fitImageWidth >= parentClientWidth) || (fit === 'contain' && fitImageWidth < parentClientWidth)) {
                setContentStyleProperty('top', '0');
                setContentStyleProperty('marginTop', '0');
                setContentStyleProperty('maxWidth', 'none');
                setContentStyleProperty('width', fitImageWidth + 'px');
                setContentStyleProperty('height', parentClientHeight + 'px');
                if (position) {
                    setPosition(fitImageWidth, 'x', position);
                }
            }
            else {
                setContentStyleProperty('width', '100%');
                setContentStyleProperty('height', 'auto');
                setContentStyleProperty('left', '0');
                setContentStyleProperty('marginLeft', '0');
                if (position) {
                    setPosition(fitImageHeight, 'y', position);
                }
            }
        }
    };
    const debouncedResizeHandler = debounce(setObjectFitStyles, 50);
    React.useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);
        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
            debouncedResizeHandler.clear();
        };
    }, [debouncedResizeHandler]);
    const setPosition = (imageSide, axis, objectPosition) => {
        let position;
        let other;
        let start;
        let end;
        let side;
        const objectPos = objectPosition.split(' ');
        if (objectPos.length < 2) {
            objectPos[1] = objectPos[0];
        }
        if (axis === 'x') {
            position = objectPos[0];
            other = objectPos[1];
            start = 'left';
            end = 'right';
            side = imageSide;
        }
        else if (axis === 'y') {
            position = objectPos[1];
            other = objectPos[0];
            start = 'top';
            end = 'bottom';
            side = imageSide;
        }
        else {
            return;
        }
        if (position === start || other === start) {
            setContentStyleProperty(start, '0');
            return;
        }
        if (position === end || other === end) {
            setContentStyleProperty(end, '0');
            return;
        }
        if (position === 'center' || position === '50%' || other === 'center' || other === '50%') {
            setContentStyleProperty(start, '50%');
            setContentStyleProperty('margin' + start.charAt(0).toUpperCase() + start.substring(1), side / -2 + 'px');
            return;
        }
        // Percentage values
        if (position.indexOf('%') >= 0) {
            // tslint:disable-next-line:radix
            position = parseInt(position);
            if (position < 50) {
                setContentStyleProperty(end, position + '%');
                setContentStyleProperty('margin' + start.charAt(0).toUpperCase() + start.substring(1), position / -100 + 'px');
            }
            else {
                position = 100 - position;
                setContentStyleProperty(end, position + '%');
                setContentStyleProperty('margin' + end.charAt(0).toUpperCase() + end.substring(1), position / -100 + 'px');
            }
        }
        else {
            // length-based values
            setContentStyleProperty(start, position);
        }
    };
    const mapChildren = () => {
        const { Image } = props.components;
        return React.Children.toArray(props.children)
            .filter((child) => {
            return React.isValidElement(child) && child.type === Image;
        })
            .map((child) => {
            return React.cloneElement(child, {
                className: BEM(child.props.className),
                imageRef: (el) => (imageRef = el),
                onLoad: (e) => onLoad(e),
                style: !browserSupport ? style : undefined,
            });
        });
    };
    return React.createElement(React.Fragment, null, mapChildren());
};
export default ObjectFitCore;
